/* eslint-disable react-hooks/exhaustive-deps */
import Chart, { EChartsOption } from "../../../components/Chart";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useSpuPoints, {
  PerformanceFormField,
  SpuPointsDataset,
  SpuPointsInfo,
} from "../../../stores/spuPoints";
import { useEffect, useRef, useState } from "react";
import {
  parseSpuPointsDataset,
  calcYAxis,
  createAuxiliaryData,
  createDiameterData,
  createSpeedData,
  createFrequencyData,
  precision,
} from "../../../utils";
import { LineSeriesOption } from "echarts/charts";
import { genRenderItem, renderLabel, exportTemplate } from "../../../libs/echar";
import { EChartsType } from "echarts/core";
import { DataPoint } from "regression";
import useSpuPdfParam from "../../../stores/spuPdfParam";
import {
  solveViscousAnchoredCurve
} from "../../SpuChart/viscosity/corrections";
import {
  buildBasePowerDisplayCurve,
  buildExtendedPowerDisplayCurve
} from "../../SpuChart/viscosity/powerCurve";
import { DisplayUnits } from "../../SpuChart/displayUnits/definitions";
import {
  buildPerformanceBaseDisplayData,
  buildPerformanceDisplayModel,
  convertFlowHeadSeries
} from "../../SpuChart/displayUnits/adapter";
import {
  buildIntegerAxisLabelFormatter,
  FLOW_AXIS_SCALE_DIVISOR,
  formatFlowAxisName,
  formatDiameterDisplayNumber,
  formatDisplayMetricNumber,
  formatPowerDisplayValue,
  shouldUseFlowAxisHundreds,
} from "../../SpuChart/displayUnits/displayFormatters";
import {
  convertNpshrFromCanonical,
  formatAxisLabel,
  getDisplayUnitLabel
} from "../../SpuChart/displayUnits/converters";
import {
  boardCurveLabelStyle,
  boardMarkerStyles,
  createBoardGrid,
  createBoardTooltip,
  createBoardToolbox,
  createBoardValueAxis,
  ndBoardTheme
} from "../../SpuChart/boardTheme";
import { buildLeadingDashedSeries } from "../../SpuChart/leadingSegments";
import { buildPerformanceChartDebugPayload } from "../../SpuChart/chartDebug";
import PerformanceInfoOverlay, {
  PerformanceInfoOverlayItem
} from "../../SpuChart/components/PerformanceInfoOverlay";
import {
  downloadPerformanceChartImage,
  exportPerformanceChartImage
} from "../../SpuChart/components/exportPerformanceChartImage";

// 性能与多速度

interface PerformanceSpeedChartProps {
  name: string;
  info: SpuPointsInfo;
  dataset: SpuPointsDataset;
  performance: PerformanceFormField;
  multiSpeed: number[];
  displayUnits: DisplayUnits;
}

declare global {
  interface Window {
    __xzpPerformanceCurveDebug?: any;
  }
}

type ChartSetOptionMode = "merge" | "replace" | "clear-then-set";

const parseDebugSeriesFilter = (raw: string | null): string[] =>
  (raw || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const PerformanceSpeedChart = ({
  name,
  info,
  dataset,
  performance,
  multiSpeed,
  displayUnits,
}: PerformanceSpeedChartProps) => {
  const { t } = useTranslation();
  const { setPerformanceSpeedResult } = useSpuPoints();
  const [debugTrace, setDebugTrace] = useState<any>(null);
  const calculationDebugRef = useRef<any>(null);
  const [overlayTopItems, setOverlayTopItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const [overlayBottomItems, setOverlayBottomItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const chartInstanceRef = useRef<EChartsType | null>(null);
  const overlayTopItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const overlayBottomItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const [searchParams] = useSearchParams();
  const chartDebugConfig: {
    disableLeadingSegments: boolean;
    disableBaseSmooth: boolean;
    showRenderTrace: boolean;
    rawBaseSeries: boolean;
    disableTooltip: boolean;
    setOptionMode: ChartSetOptionMode;
    seriesFilter: string[];
  } = {
    disableLeadingSegments:
      searchParams.get("debugDisableLeadingSegments") === "1" ||
      searchParams.get("debugDisableLeadingSegments") === "true",
    disableBaseSmooth:
      searchParams.get("debugDisableBaseSmooth") === "1" ||
      searchParams.get("debugDisableBaseSmooth") === "true",
    showRenderTrace:
      searchParams.get("debugShowRenderTrace") === "1" ||
      searchParams.get("debugShowRenderTrace") === "true",
    rawBaseSeries:
      searchParams.get("debugRawBaseSeries") === "1" ||
      searchParams.get("debugRawBaseSeries") === "true",
    disableTooltip:
      searchParams.get("debugDisableTooltip") === "1" ||
      searchParams.get("debugDisableTooltip") === "true",
    setOptionMode:
      searchParams.get("debugSetOptionMode") === "replace" ||
      searchParams.get("debugSetOptionMode") === "clear-then-set"
        ? (searchParams.get("debugSetOptionMode") as "replace" | "clear-then-set")
        : "merge",
    seriesFilter: parseDebugSeriesFilter(searchParams.get("debugSeries")),
  };

  useEffect(() => {
    overlayTopItemsRef.current = overlayTopItems;
    overlayBottomItemsRef.current = overlayBottomItems;
  }, [overlayTopItems, overlayBottomItems]);

  const handleSaveChartImage = () => {
    if (!chartInstanceRef.current) {
      return;
    }

    downloadPerformanceChartImage(
      chartInstanceRef.current,
      name,
      overlayTopItemsRef.current,
      overlayBottomItemsRef.current
    );
  };

  const initialOption: EChartsOption = {
    backgroundColor: ndBoardTheme.background,
    tooltip: chartDebugConfig.disableTooltip ? { show: false, trigger: "none" } : createBoardTooltip(),
    toolbox: createBoardToolbox(name, handleSaveChartImage),
    grid: [
      createBoardGrid({ left: 44, right: 44, top: 38, bottom: "40%" }),
      createBoardGrid({ left: 44, right: 44, top: "70%", bottom: 34 })
    ],
    xAxis: [
      createBoardValueAxis({
        name: formatAxisLabel("flow", displayUnits.flow),
        splitNumber: 10,
        nameGap: 18,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("flow"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
          },
        },
      }),
      createBoardValueAxis({
        name: formatAxisLabel("flow", displayUnits.flow),
        gridIndex: 1,
        splitNumber: 10,
        nameGap: 18,
        axisLabel: {
          formatter: buildIntegerAxisLabelFormatter("flow"),
        },
        axisPointer: {
          label: {
            formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
          },
        },
      }),
    ],
  };

  const [option, setOption] = useState(initialOption);

  useEffect(() => {
    // 对原始数据进行线性回归
    const { QH, QP, QEFF, QNPSH } = parseSpuPointsDataset(dataset);
    const displayDataSource = buildPerformanceBaseDisplayData({ QH, QP, QEFF, QNPSH } as any, displayUnits);
    const displayBasePowerData = buildBasePowerDisplayCurve(displayDataSource.QP);
    const flowAxisMax = Math.max(
      0,
      ...displayDataSource.QH.map((point) => point[0] || 0),
      ...displayDataSource.QP.map((point) => point[0] || 0),
      ...displayDataSource.QEFF.map((point) => point[0] || 0),
      ...displayDataSource.QNPSH.map((point) => point[0] || 0)
    );
    const useScaledFlowAxis = shouldUseFlowAxisHundreds(flowAxisMax);
    const flowAxisLabelFormatter = buildIntegerAxisLabelFormatter("flow", {
      scaleDivisor: useScaledFlowAxis ? FLOW_AXIS_SCALE_DIVISOR : undefined,
    });
    const flowAxisName = formatFlowAxisName(
      formatAxisLabel("flow", displayUnits.flow),
      flowAxisMax
    );
    const { quantity, head, sg, type, viscosity } = performance;
    setOverlayTopItems([]);
    setOverlayBottomItems([]);

    // 找到共有区间
    const [hMax, hInterval] = calcYAxis(
      displayDataSource.QH.map((p) => p[1]),
      40,
      1
    );
    const [pMax, pInterval] = calcYAxis(
      displayBasePowerData.map((p) => p[1]),
      20,
      1
    );

    // 创建 PerformanceSpeedChart 视图
    const rawBaseSeries: LineSeriesOption[] = [
      {
        name: "QH1",
        type: "line",
        // Keep board-style curve labels on the shared helper so empty label points
        // do not render background artifacts as a fake pale curve.
        label: renderLabel("Q-H", [20, -8], undefined, boardCurveLabelStyle),
        smooth: !chartDebugConfig.disableBaseSmooth,
        symbolSize: 0,
        lineStyle: {
          width: 1,
        },
        color: ndBoardTheme.referenceCurve,
        data: displayDataSource.QH,
        animation: false,
      },
      {
        name: "QEFF",
        type: "line",
        // Do not inline a plain boxed label here. This series depends on renderLabel
        // to isolate business label styling to the actual visible text point only.
        label: renderLabel("Q-EFF", [18, -8], undefined, boardCurveLabelStyle),
        smooth: !chartDebugConfig.disableBaseSmooth,
        symbolSize: 0,
        lineStyle: {
          width: 1,
        },
        color: ndBoardTheme.referenceCurve,
        data: displayDataSource.QEFF,
        xAxisIndex: 0,
        yAxisIndex: 1,
        animation: false,
      },
      {
        name: "QP1",
        type: "line",
        label: renderLabel("Q-P", [18, -8], undefined, boardCurveLabelStyle),
        smooth: false,
        symbolSize: 0,
        lineStyle: {
          width: 1,
        },
        color: ndBoardTheme.referenceCurve,
        data: displayBasePowerData,
        xAxisIndex: 1,
        yAxisIndex: 2,
        animation: false,
      },
      {
        name: "QNPSH",
        type: "line",
        label: renderLabel("Q-NPSHr", [20, -8], undefined, boardCurveLabelStyle),
        smooth: !chartDebugConfig.disableBaseSmooth,
        emphasis: {
          disabled: true,
        },
        symbolSize: 0,
        lineStyle: {
          width: 1,
        },
        data: displayDataSource.QNPSH,
        color: ndBoardTheme.referenceCurve,
        xAxisIndex: 1,
        yAxisIndex: 3,
        animation: false,
      },
    ];
    const initialSeries: LineSeriesOption[] = chartDebugConfig.rawBaseSeries
      ? rawBaseSeries
      : [
          ...buildLeadingDashedSeries(rawBaseSeries[0], displayDataSource.QH, {
            disabled: chartDebugConfig.disableLeadingSegments,
          }),
          ...buildLeadingDashedSeries(rawBaseSeries[1], displayDataSource.QEFF, {
            disabled: chartDebugConfig.disableLeadingSegments,
          }),
          ...buildLeadingDashedSeries(rawBaseSeries[2], displayBasePowerData, {
            disabled: chartDebugConfig.disableLeadingSegments,
          }),
          rawBaseSeries[3],
        ];

    const { speed } = info;
    const speedSeries: LineSeriesOption[] = multiSpeed.map(
      (newSpeed: number, index: number) => {
        const k = newSpeed / speed;
        const data = convertFlowHeadSeries(
          QH.dummyPoints.map((p) => [p[0] * k, p[1] * k ** 2] as DataPoint),
          displayUnits
        );

        return {
          name: `Speed ${index + 1}`,
          type: "line",
          label: renderLabel(`${newSpeed} RPM`),
          smooth: true,
          symbolSize: 0,
          lineStyle: {
            width: 1,
          },
          color: ndBoardTheme.supportCurve,
          data,
          animation: false,
        } as LineSeriesOption;
      }
    ).flatMap((config) =>
      buildLeadingDashedSeries(config, config.data as DataPoint[], {
        disabled: chartDebugConfig.disableLeadingSegments,
      })
    );

    let newOption: EChartsOption = {
      ...initialOption,
      xAxis: [
        createBoardValueAxis({
          name: flowAxisName,
          splitNumber: 10,
          nameGap: 18,
          axisLabel: {
            formatter: flowAxisLabelFormatter,
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
            },
          },
        }),
        createBoardValueAxis({
          name: flowAxisName,
          gridIndex: 1,
          splitNumber: 10,
          nameGap: 18,
          axisLabel: {
            formatter: flowAxisLabelFormatter,
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
            },
          },
        }),
      ],
      yAxis: [
        // H
        createBoardValueAxis({
          name: formatAxisLabel("head", displayUnits.head),
          min: 0,
          max: hMax,
          interval: hInterval,
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true,
            hideOverlap: true,
            formatter: buildIntegerAxisLabelFormatter("head", {
              shouldDisplay: (_value, index) => index % 5 === 0,
            }),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("head")(value, 0),
            },
          },
        }),
        // EFF
        createBoardValueAxis({
          name: "EFF(%)",
          type: "value",
          min: 0,
          max: 200,
          splitNumber: 10,
          axisPointer: {
            label: {
              show: false,
            },
          },
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true,
            hideOverlap: true,
            formatter: buildIntegerAxisLabelFormatter("efficiency", {
              shouldDisplay: (value) => value <= 100,
            }),
          }
        }),
        // P
        createBoardValueAxis({
          name: formatAxisLabel("power", displayUnits.power),
          min: 0,
          max: pMax,
          interval: pInterval,
          gridIndex: 1,
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true,
            hideOverlap: true,
            formatter: buildIntegerAxisLabelFormatter("power", {
              shouldDisplay: (_value, index) => index % 5 === 0,
            }),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("power")(value, 0),
            },
          },
        }),
        // NPSHr
        createBoardValueAxis({
          name: formatAxisLabel("npshr", displayUnits.npshr),
          gridIndex: 1,
          min: 0,
          max: convertNpshrFromCanonical(15, displayUnits.npshr) as number,
          splitNumber: 5,
          axisPointer: {
            label: {
              show: false,
            },
          },
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true,
            hideOverlap: true
          }
        }),
      ],
      series: [...initialSeries, ...speedSeries],
    };

    // 如果表单数据，则绘制 QH2/QP2/QEFF2/QNPSH2 和对应文字
    if (quantity !== undefined && head !== undefined) {
      const { range } = info;
      // 获取区间中点
      const center = (range.start + range.end) / 2;
      // 获取辅助线与QH1的交点
      const solvedCurve = solveViscousAnchoredCurve({
        quantity,
        head,
        sg,
        viscosityCanonical: viscosity,
        speed: info.speed,
        type,
        qhPoints: QH.dummyPoints,
        qeffPoints: QEFF.dummyPoints,
        qnpshPoints: QNPSH ? QNPSH.dummyPoints : [],
        qhRawPoints: QH.points as DataPoint[],
        qeffRawPoints: QEFF.points as DataPoint[],
        qhEquation: QH.equation,
        center
      });
      if (solvedCurve) {
        // 生成辅助线数据
        const auxData = createAuxiliaryData(
          quantity,
          head,
          0,
          solvedCurve.waterDutyPoint.quantity,
          type
        );

        const k = solvedCurve.k;
        const correctedQH2Data = solvedCurve.viscousCurves.qH2v;
        const correctedQEFF2Data = solvedCurve.viscousCurves.qEFF2v;
        const correctedQNPSH2Data = solvedCurve.viscousCurves.qNPSH2v;
        // 计算叶轮尺寸
        const { diameter, diameterUnit } = createDiameterData(
          k,
          info.diameter,
          type
        );

        // 计算转速
        const speed = createSpeedData(k, info.speed, type);

        // 计算频率
        const frequency = createFrequencyData(k, info.frequency, type);

        const correctedDutyPoint = solvedCurve.correctedDutyPointResult;

        const correctedQP2Data = buildExtendedPowerDisplayCurve(
          displayDataSource.QP,
          solvedCurve.viscousCurves.qP2vTrusted,
          {
            anchorPoint: [quantity, correctedDutyPoint.power]
          }
        );
        calculationDebugRef.current = {
          k: solvedCurve.k,
          waterDutyPoint: solvedCurve.waterDutyPoint,
          viscousDutyPoint: solvedCurve.viscousDutyPoint,
          correctedDutyPointResult: solvedCurve.correctedDutyPointResult,
          viscosity: solvedCurve.debug,
          powerCurve: {
            baseQPDisplaySample: displayDataSource.QP?.slice?.(0, 8),
            qP2TrustedSample: solvedCurve.debug.qP2vTrustedSample,
            qP2PhysicalSample: solvedCurve.debug.qP2vSample,
            qP2DisplaySample: correctedQP2Data?.filter((_, index) => index % Math.max(1, Math.floor(correctedQP2Data.length / 8)) === 0).slice(0, 8),
          },
        };

        // 更新表单
        setPerformanceSpeedResult({
          diameter,
          diameterUnit,
          speed,
          frequency,
          power: correctedDutyPoint.power,
          npsh: correctedDutyPoint.npsh,
          eff: correctedDutyPoint.eff,
        });

        const displayModel = buildPerformanceDisplayModel({
          displayUnits,
          quantity,
          head,
          power: correctedDutyPoint.power,
          npsh: correctedDutyPoint.npsh ?? null,
          diameter,
          diameterUnit,
          auxData,
          qH2Data: correctedQH2Data,
          qEFF2Data: correctedQEFF2Data,
          qNPSH2Data: correctedQNPSH2Data,
          qP2Data: correctedQP2Data,
        });

        newOption = {
          ...newOption,
          series: [
            ...initialSeries,
            ...speedSeries,
            {
              name: "aux",
              type: "line",
              smooth: true,
              emphasis: {
                disabled: true,
              },
              symbolSize: 0,
              lineStyle: {
                width: 1,
              },
              color: ndBoardTheme.auxiliaryCurve,
              data: displayModel.auxData,
              animation: false,
            },
            ...buildLeadingDashedSeries({
              name: "QH2",
              type: "line",
              smooth: true,
              symbolSize: 0,
              lineStyle: { width: 1.6 },
              color: ndBoardTheme.activeCurve,
              label: renderLabel(
                type === 0
                  ? `${precision(speed, 0)} RPM`
                  : `${formatDiameterDisplayNumber(displayModel.diameter, displayModel.diameterDisplayUnit)} ${displayModel.diameterDisplayUnit}`
              ),
              animation: false,
            }, displayModel.qH2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries({
              name: "QEFF2",
              type: "line",
              smooth: true,
              symbolSize: 0,
              lineStyle: { width: 1 },
              xAxisIndex: 0,
              yAxisIndex: 1,
              color: ndBoardTheme.supportCurve,
              animation: false,
            }, displayModel.qEFF2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries({
              name: "QP2",
              type: "line",
              smooth: false,
              symbolSize: 0,
              lineStyle: { width: 1 },
              xAxisIndex: 1,
              yAxisIndex: 2,
              color: ndBoardTheme.supportCurve,
              animation: false,
            }, displayModel.qP2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle1", displayModel.quantity, displayModel.head, undefined, {
                markerStyles: boardMarkerStyles,
              }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head,
                },
              ],
              z: 100,
            },
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle2", displayModel.quantity, displayModel.head, undefined, {
                markerStyles: boardMarkerStyles,
              }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head,
                },
              ],
              z: 101,
            },
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle3", displayModel.quantity, displayModel.head, undefined, {
                markerStyles: boardMarkerStyles,
              }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head,
                },
              ],
              z: 102,
            },
            {
              name: "QNPSH2",
              type: "line",
              smooth: true,
              emphasis: {
                disabled: true,
              },
              symbolSize: 0,
              lineStyle: { width: 1 },
              xAxisIndex: 1,
              yAxisIndex: 3,
              color: ndBoardTheme.supportCurve,
              data: displayModel.qNPSH2Data,
              animation: false,
            },
          ],
        };

        setOverlayTopItems([
          { label: "Q", value: formatDisplayMetricNumber("flow", displayModel.quantity), unit: getDisplayUnitLabel("flow", displayUnits.flow) },
          { label: "H", value: formatDisplayMetricNumber("head", displayModel.head), unit: getDisplayUnitLabel("head", displayUnits.head) },
          {
            label: t("chart.diameter"),
            value: formatDiameterDisplayNumber(displayModel.diameter, displayModel.diameterDisplayUnit),
            unit: displayModel.diameterDisplayUnit,
          },
          {
            label: t("chart.speed"),
            value: precision(speed, 0),
            unit: "rpm",
          },
          {
            label: t("chart.efficiency"),
            value: formatDisplayMetricNumber("efficiency", correctedDutyPoint.eff),
            unit: "%",
          },
          {
            label: t("chart.sg"),
            value: precision(sg || 1, 2),
          },
        ]);

        setOverlayBottomItems([
          { label: "P", value: formatPowerDisplayValue(displayModel.power), unit: getDisplayUnitLabel("power", displayUnits.power) },
          {
            label: "NPSHr",
            value: displayModel.npsh !== null && displayModel.npsh !== undefined ? formatDisplayMetricNumber("npshr", displayModel.npsh) : displayModel.npsh,
            unit: getDisplayUnitLabel("npshr", displayUnits.npshr),
          },
        ]);
      }
    }

    if (chartDebugConfig.seriesFilter.length > 0 && Array.isArray(newOption.series)) {
      newOption = {
        ...newOption,
        series: newOption.series.filter((series: any) =>
          chartDebugConfig.seriesFilter.includes(series?.name)
        ),
      };
    }

    setOption(newOption);
  }, [
    JSON.stringify(performance),
    JSON.stringify(multiSpeed),
    JSON.stringify(dataset),
    JSON.stringify(displayUnits),
    chartDebugConfig.disableBaseSmooth,
    JSON.stringify(chartDebugConfig.seriesFilter),
  ]);

  const { setPerformanceSpeedImage } = useSpuPdfParam();

  const handleChartChange = (instance: EChartsType) => {
    chartInstanceRef.current = instance;
    try {
      const debugPayload = buildPerformanceChartDebugPayload(instance, {
        instance,
        renderType: "performance-speed",
        chartDebugConfig,
        calculationDebug: calculationDebugRef.current,
      });

      window.__xzpPerformanceCurveDebug = debugPayload;
      setDebugTrace(debugPayload);
      console.log("__xzpPerformanceCurveDebug", debugPayload);
    } catch (error) {
      console.warn("__xzpPerformanceCurveDebug_error", error);
    }

    const result = exportPerformanceChartImage(
      instance,
      exportTemplate as any,
      overlayTopItems,
      overlayBottomItems
    );
    setPerformanceSpeedImage(result);
  };

  return (
    <>
      <div className="performance-chart-main" style={{ position: "relative", width: 500, height: 700, margin: "auto" }}>
        <Chart
          option={option}
          notMerge
          setOptionMode={chartDebugConfig.setOptionMode}
          style={{ width: 500, height: 700, margin: "auto" }}
          onChange={handleChartChange}
        />
        <PerformanceInfoOverlay
          topItems={overlayTopItems}
          bottomItems={overlayBottomItems}
        />
      </div>
      {chartDebugConfig.showRenderTrace && debugTrace && (
        <pre
          style={{
            width: 500,
            margin: "12px auto 0",
            maxHeight: 220,
            overflow: "auto",
            background: "#0f1720",
            color: "#d7e3ef",
            padding: 12,
            fontSize: 11,
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
          }}
        >
          {JSON.stringify(
            {
              explicitSeriesCount: debugTrace.explicitSeriesCount,
              displayLineObjectCount: debugTrace.displayLineObjectCount,
              domCanvasSummary: debugTrace.domCanvasSummary,
              xAxisSummary: debugTrace.xAxisSummary,
              yAxisSummary: debugTrace.yAxisSummary,
              optionSeries: debugTrace.optionSeries?.map((series: any) => ({
                name: series.name,
                color: series.color,
                lineDash: series.lineDash,
                dataCount: series.dataCount,
              })),
              slantedLineSegmentsByStroke: debugTrace.slantedLineSegmentsByStroke,
            },
            null,
            2
          )}
        </pre>
      )}
    </>
  );
};

export default PerformanceSpeedChart;
